import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/ko';

dayjs.extend(relativeTime);
dayjs.locale('ko');

/**
 * 숫자 유틸 공통 규칙:
 *  - null / undefined / 빈 문자열 → 기본값
 *  - NUMERIC pg 컬럼이 string 으로 반환되는 경우도 안전하게 처리
 *  - NaN 은 기본값으로 fallback (예전: .toFixed 크래시 케이스)
 */
type Numeric = number | string | null | undefined;

function toNumber(n: Numeric): number | null {
  if (n === null || n === undefined || n === '') return null;
  const num = typeof n === 'number' ? n : Number(n);
  return Number.isFinite(num) ? num : null;
}

/** 숫자에 천 단위 콤마 */
export function comma(n: Numeric): string {
  const num = toNumber(n);
  if (num === null) return '0';
  return num.toLocaleString('ko-KR');
}

/** 가격 표시 (원화 기본) */
export function price(n: Numeric, currency = '원'): string {
  const num = toNumber(n);
  if (num === null) return '-';
  return `${num.toLocaleString('ko-KR')}${currency}`;
}

/** 증감률 ±x.xx% */
export function rate(n: Numeric, digits = 2): string {
  const num = toNumber(n);
  if (num === null) return '-';
  const sign = num > 0 ? '+' : '';
  return `${sign}${num.toFixed(digits)}%`;
}

/** 간략 숫자 (1.2K / 3.4M) */
export function compact(n: Numeric): string {
  const num = toNumber(n);
  if (num === null) return '0';
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return String(num);
}

/**
 * 작성일 표기 — 단순 2분기 규칙
 * - 오늘(로컬 기준 같은 날짜) → HH:MM
 * - 그 외 → YY.MM.DD
 *
 * '방금 전' / 'N시간 전' / 'N일 전' 표기는 사용하지 않음.
 */
export function timeAgo(date: string | Date): string {
  const now = new Date();
  const d = new Date(date);
  const isToday = now.toDateString() === d.toDateString();
  if (isToday) {
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    return `${hh}:${mi}:${ss}`;
  }
  const yy = String(d.getFullYear()).slice(2);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yy}.${mm}.${dd}`;
}

/** @deprecated `timeAgo` 와 동일 — 호환성 위해 유지 */
export const timeAgoBoard = timeAgo;

/** 절대 시간 (2026-04-13 13:45) */
export function dateTime(date: string | Date): string {
  return dayjs(date).format('YYYY-MM-DD HH:mm');
}

/** 날짜만 (2026-04-13) — 시/분 제외 */
export function dateOnly(date: string | Date): string {
  return dayjs(date).format('YYYY-MM-DD');
}

/** YYYY-MM-DD */
export function isoDate(date: string | Date): string {
  return dayjs(date).format('YYYY-MM-DD');
}

/** 닉네임 마스킹: abc***ef */
export function maskNickname(nickname: string): string {
  if (nickname.length <= 3) return nickname[0] + '**';
  return nickname.slice(0, 2) + '***' + nickname.slice(-2);
}
